import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Home,
  Target,
  BookOpen,
  FileText,
  BarChart3,
  Settings,
  Sparkles,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';

interface Command {
  id: string;
  label: string;
  description?: string;
  category: 'navigation' | 'actions' | 'recent' | 'ai';
  action: () => void;
  icon: any;
  shortcut?: string;
}

export const CommandPalette = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Fetch recent navigation events
  const { data: recentPages } = useQuery({
    queryKey: ['recent-navigation', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from('navigation_events')
        .select('route, metadata')
        .eq('user_id', user.id)
        .eq('action', 'visit')
        .order('created_at', { ascending: false })
        .limit(10);
      
      // Deduplicate routes
      const seen = new Set();
      return data?.filter(item => {
        if (seen.has(item.route)) return false;
        seen.add(item.route);
        return true;
      }).slice(0, 5) || [];
    },
    enabled: !!user,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === 'j' || e.key === 'J') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Track command palette opens
  useEffect(() => {
    if (open && user) {
      supabase.from('navigation_events').insert({
        user_id: user.id,
        route: location.pathname,
        action: 'command_palette_opened',
        metadata: {}
      });
    }
  }, [open, user, location.pathname]);

  const trackCommand = async (command: Command) => {
    if (user) {
      await supabase.from('navigation_events').insert({
        user_id: user.id,
        route: location.pathname,
        action: 'command_executed',
        metadata: { command_id: command.id, command_label: command.label }
      });
    }
  };

  const commands: Command[] = [
    // Navigation
    {
      id: 'nav-home',
      label: 'Go to Dashboard',
      category: 'navigation',
      action: () => navigate('/dashboard'),
      icon: Home,
      shortcut: 'G then D'
    },
    {
      id: 'nav-practice',
      label: 'Go to Practice',
      category: 'navigation',
      action: () => navigate('/practice'),
      icon: Target,
      shortcut: 'G then P'
    },
    {
      id: 'nav-notes',
      label: 'Go to Notes',
      category: 'navigation',
      action: () => navigate('/notes'),
      icon: BookOpen,
      shortcut: 'G then N'
    },
    {
      id: 'nav-mock',
      label: 'Go to Mock Exams',
      category: 'navigation',
      action: () => navigate('/mock'),
      icon: FileText,
      shortcut: 'G then M'
    },
    {
      id: 'nav-admin',
      label: 'Go to Admin',
      category: 'navigation',
      action: () => navigate('/admin'),
      icon: Settings,
    },
    
    // Actions
    {
      id: 'action-practice-easy',
      label: 'Start Easy Practice',
      description: 'Practice with difficulty level 1-2',
      category: 'actions',
      action: () => navigate('/practice?difficulty=1-2'),
      icon: Target,
    },
    {
      id: 'action-practice-hard',
      label: 'Start Hard Practice',
      description: 'Challenge yourself with level 4-5 questions',
      category: 'actions',
      action: () => navigate('/practice?difficulty=4-5'),
      icon: TrendingUp,
    },
    {
      id: 'action-mock-exam',
      label: 'Start Mock Exam',
      description: 'Begin a timed mock examination',
      category: 'actions',
      action: () => navigate('/mock'),
      icon: FileText,
    },
    {
      id: 'action-view-insights',
      label: 'View AI Insights',
      description: 'Get personalized study recommendations',
      category: 'ai',
      action: () => navigate('/dashboard'),
      icon: Sparkles,
    },
    {
      id: 'action-continue-learning',
      label: 'Continue Learning',
      description: 'Pick up where you left off',
      category: 'actions',
      action: () => {
        const lastRoute = localStorage.getItem('last-learning-route');
        navigate(lastRoute || '/practice');
      },
      icon: Clock,
    },
  ];

  // Add recent pages as commands
  const recentCommands: Command[] = recentPages?.map((page, idx) => ({
    id: `recent-${idx}`,
    label: `Back to ${page.route}`,
    category: 'recent' as const,
    action: () => navigate(page.route),
    icon: Clock,
  })) || [];

  const allCommands = [...commands, ...recentCommands];

  const filteredCommands = query
    ? allCommands.filter(cmd =>
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.description?.toLowerCase().includes(query.toLowerCase())
      )
    : allCommands;

  const groupedCommands = {
    navigation: filteredCommands.filter(c => c.category === 'navigation'),
    actions: filteredCommands.filter(c => c.category === 'actions'),
    recent: filteredCommands.filter(c => c.category === 'recent'),
    ai: filteredCommands.filter(c => c.category === 'ai'),
  };

  const handleSelect = async (command: Command) => {
    await trackCommand(command);
    command.action();
    setOpen(false);
  };

  return (
    <>
      {/* Trigger hint */}
      <div className="fixed bottom-4 right-4 z-40 hidden md:block">
        <button
          onClick={() => setOpen(true)}
          className="px-3 py-2 text-xs bg-background border rounded-lg shadow-lg hover:bg-accent transition-colors flex items-center gap-2"
        >
          <span className="text-muted-foreground">Quick Actions</span>
          <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
            <span className="text-xs">⌘</span>J
          </kbd>
        </button>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Type a command or search..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {groupedCommands.navigation.length > 0 && (
            <CommandGroup heading="Navigation">
              {groupedCommands.navigation.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  onSelect={() => handleSelect(cmd)}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <cmd.icon className="h-4 w-4" />
                    <span>{cmd.label}</span>
                  </div>
                  {cmd.shortcut && (
                    <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
                      {cmd.shortcut}
                    </kbd>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {groupedCommands.actions.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Quick Actions">
                {groupedCommands.actions.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    onSelect={() => handleSelect(cmd)}
                  >
                    <cmd.icon className="mr-2 h-4 w-4" />
                    <div>
                      <p>{cmd.label}</p>
                      {cmd.description && (
                        <p className="text-xs text-muted-foreground">
                          {cmd.description}
                        </p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {groupedCommands.ai.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="AI Suggestions">
                {groupedCommands.ai.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    onSelect={() => handleSelect(cmd)}
                  >
                    <cmd.icon className="mr-2 h-4 w-4 text-primary" />
                    <div>
                      <p>{cmd.label}</p>
                      {cmd.description && (
                        <p className="text-xs text-muted-foreground">
                          {cmd.description}
                        </p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {groupedCommands.recent.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Recent Pages">
                {groupedCommands.recent.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    onSelect={() => handleSelect(cmd)}
                  >
                    <cmd.icon className="mr-2 h-4 w-4" />
                    <span>{cmd.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
